import { TextField } from '@mui/material';
import {
    ChangeEvent,
    KeyboardEvent,
    RefObject,
    forwardRef,
    useCallback,
} from 'react';

import { errorColor, errorDisabledColor } from './QuantityButtonStyles';

interface QuantityInputProps {
    value: string;
    onChange: (value: string) => void;
    onFocus: () => void;
    onBlur: () => void;
    onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
    disabled?: boolean;
    error?: boolean;
    errorDescribedBy?: string;
    maxLength?: number;
    inputRef?: RefObject<HTMLInputElement>;
    'data-testid'?: string;
}

const MAX_CHARACTERS = 4;

export const QuantityInput = forwardRef<HTMLInputElement, QuantityInputProps>(
    (
        {
            value,
            onChange,
            onFocus,
            onBlur,
            onKeyDown,
            disabled = false,
            error = false,
            errorDescribedBy,
            maxLength = MAX_CHARACTERS,
            inputRef,
            'data-testid': dataTestId = 'quantity-input',
        },
        ref,
    ) => {
        const handleInputChange = useCallback(
            (event: ChangeEvent<HTMLInputElement>) => {
                const newValue = event.target.value;

                // Only allow digits
                if (!/^\d*$/.test(newValue)) {
                    return;
                }

                // Prevent leading zeros (only allow 1-9 as first digit)
                if (newValue.length > 1 && newValue[0] === '0') {
                    return;
                }

                // Limit to max characters
                if (newValue.length > maxLength) {
                    return;
                }

                onChange(newValue);
            },
            [onChange, maxLength],
        );

        const handleFocus = useCallback(
            (event: React.FocusEvent<HTMLInputElement>) => {
                event.target.select();
                onFocus();
            },
            [onFocus],
        );

        return (
            <TextField
                value={value}
                onChange={handleInputChange}
                onKeyDown={onKeyDown}
                onFocus={handleFocus}
                onBlur={onBlur}
                disabled={disabled}
                variant="standard"
                size="small"
                inputProps={{
                    style: {
                        textAlign: 'center',
                        fontSize: '1.4rem',
                        fontWeight: 600,
                        padding: '0',
                        height: '32px',
                        boxSizing: 'border-box',
                        borderRadius: '0',
                        width: value.length >= 3 ? '60px' : '40px',
                        minWidth: value.length >= 3 ? '60px' : '40px',
                    },
                    maxLength,
                    autoComplete: 'off',
                    'data-testid': dataTestId,
                    'aria-invalid': error || undefined,
                    'aria-describedby':
                        error && errorDescribedBy
                            ? errorDescribedBy
                            : undefined,
                }}
                sx={{
                    '& .MuiInput-root': {
                        border: 'none',
                        '&:before': { borderBottom: 'none' },
                        '&:after': { borderBottom: 'none' },
                        '&:hover:not(.Mui-disabled):before': {
                            borderBottom: 'none',
                        },
                        '&.Mui-disabled:before': {
                            borderBottom: 'none',
                        },
                        '&.Mui-disabled:after': {
                            borderBottom: 'none',
                        },
                    },
                    '& .MuiInput-input': {
                        color: error ? 'error.main' : 'rgba(0, 0, 0, 0.87)',
                        backgroundColor: error ? '#FFF' : undefined,
                        border: '1px solid',
                        borderColor: error ? '#FEEBED' : '#DFDFDF',
                        '&.Mui-disabled': {
                            backgroundColor: error ? '#FFF' : '#F4F4F4',
                            color: error
                                ? errorDisabledColor
                                : 'rgba(56, 40, 40, 0.2)',
                            WebkitTextFillColor: error
                                ? errorDisabledColor
                                : 'rgba(56, 40, 40, 0.2)',
                        },
                        '&:focus:not(.Mui-disabled)': {
                            borderColor: error ? errorColor : '#3F6EB7',
                        },
                        '&:hover:not(.Mui-disabled)': {
                            borderColor: error ? errorColor : '#3F6EB7',
                        },
                    },
                }}
                inputRef={inputRef || ref}
            />
        );
    },
);

QuantityInput.displayName = 'QuantityInput';
